import './filter-controller.css';

import throttle from 'lodash-es/throttle';

import { forwardEnterToClick, on, unByKey } from 'cadenza/utils/event-util';
import { PARAMETER_CONDITION_HASH } from 'cadenza/api-client/api-constants';
import { setParam, getUrlParam } from 'cadenza/utils/url-param-utils';
import {
  CONDITION_CHANGE,
  CONDITIONS_HIERARCHIES_CHANGE,
  CHANGED,
  APPLY_FILTERS,
  FILTERS_APPLIED
} from 'cadenza/conditions/event-types';
import { HIERARCHY_CHANGE } from 'cadenza/hierarchy-selection/event-types';
import { storeRequestParameterValue } from 'cadenza/api-client/processing-chain-api';
import { logger } from 'cadenza/utils/logging';
import { getOnDemandViewElement } from 'cadenza/on-demand-view/on-demand-view';
import { debounce } from 'cadenza/utils/debounce';
import { selector as conditionGroupSelector } from 'cadenza/conditions/condition-group';

const NAME = 'd-filter-controller';
const MAX_TIME_POINTERUP_INTERVAL = 500;

export class FilterController extends HTMLElement {

  #initialHash;
  #hash;
  #validity = { valid: true };
  /** conditions that were disabled for consistency (avoid race conditions), so they can be re-enabled precisely.*/
  #temporarilyDisabledConditions = [];
  #disableConditionsDebounced = debounce(disableConditions);
  #setDelayNotificationDebounced = debounce(setDelayNotification);

  constructor (initialHash, hash) {
    super();
    this.#initialHash = initialHash;
    this.#hash = hash;
  }

  connectedCallback () {
    this.classList.add(NAME);
    this._eventKeys = [
      on(this, CONDITION_CHANGE, event => this._onConditionChanged(event)),
      on(this, HIERARCHY_CHANGE, (event) => this._onHierarchyChanged(event))
    ];
    this._wireToggleFilterButton();
    // if the on demand view element got added before the filter controller, connect it
    getOnDemandViewElement()?.setFilterController(this); // ?, because there need not be an onDemandViewElement
    setTimeout(() => { // Light DOM is ready
      if (this.hasHeader) {
        this._resetAllBtn = this.closest('.d-sidebar').querySelector('.d-sidebar-reset');
        this._eventKeys.push(on(this._resetAllBtn, 'click', event => this._onResetAllBtnClick(event)));
        this._updateResetAllVisibility();
      }
      this._enablingConditions = [ ...this.querySelectorAll('.condition') ]
        .filter(condition => condition.enables && condition.enables.length > 0);
      this._singleSelectionGroups = this.querySelectorAll(`${conditionGroupSelector}[singleSelection]`);
      this._updateAllConditionsValidity();
      this._updateConditionEnabledState();

      const sidebarSectionHeaders =
        this.querySelectorAll('.d-sidebar-content-filter-panel .d-sidebar-section-title');
      sidebarSectionHeaders.forEach((header) => {
        header.addEventListener('keydown', forwardEnterToClick);
        header.addEventListener('click', () => {
          const section = document.getElementById(header.getAttribute('aria-controls'));
          this._toggleSection(header, section);
        });
      });
    });
  }

  get conditionValuesSetHash () {
    if (this.isUpdateParams) {
      return getUrlParam({ name: PARAMETER_CONDITION_HASH });
    } else {
      return this.#hash;
    }
  }

  disconnectedCallback () {
    unByKey(this._eventKeys);
  }

  notifyApplyFilters () {
    this.dispatchEvent(new CustomEvent(APPLY_FILTERS));
  }

  notifyFiltersApplied () {
    this.dispatchEvent(new CustomEvent(FILTERS_APPLIED));
  }

  get hasHeader () {
    return this.getAttribute('has-header') === 'true';
  }

  get layerId () {
    return this.getAttribute('layer-id');
  }

  get isUpdateParams () {
    return this.getAttribute('update-search-params') === 'true';
  }

  get initialConditionValuesSetHash () {
    return this.#initialHash || this.getAttribute('initial-hash');
  }

  get validity () {
    return this.#validity;
  }

  /**
   * Disable all other enabled conditions if there is at least one
   * context sensitive condition: if there are context sensitive
   * conditions, the backend must do database queries which can be
   * slow, so we have to disable all other conditions until the values
   * are set, otherwise selection could be inconsistent.
   *
   * This only disables *other* conditions, because users are likely
   * to do multiple changes in a row.
   *
   * @param {object[]} excluded - a list of conditions too ignore
   */
  disableOtherConditionsIfNeededForConsistency (excluded) {
    getOnDemandViewElement()?.setEnabled(false); // ?, because there need not be an onDemandViewElement
    const allOtherConditions = [ ...this.querySelectorAll('.condition') ]
      .filter(condition => !excluded.includes(condition));
    const hasContextSensitiveConditions = allOtherConditions
      .filter(condition => condition.contextSensitive).length > 0;
    if (hasContextSensitiveConditions) {
      const enabledConditions = allOtherConditions
        .filter(condition => condition.enabled);
      this.#disableConditionsDebounced(enabledConditions);
      this.#setDelayNotificationDebounced(excluded, true);
      if (this.#temporarilyDisabledConditions.length === 0) {
        this.#temporarilyDisabledConditions = enabledConditions;
      }
    }
  }

  setConditionsDisabledForConsistency (enabled, activeCondition) {
    const conditions = this.#temporarilyDisabledConditions;
    conditions
      .forEach(condition => {
        condition.enabled = enabled;
      });
    if (enabled) {
      this.#disableConditionsDebounced.cancel();
      this.#setDelayNotificationDebounced.cancel();
      setDelayNotification([ activeCondition ], false);
      this.#temporarilyDisabledConditions = [];
      getOnDemandViewElement()?.setEnabled(true);
    }
  }

  _onResetAllBtnClick () {
    const resetConditions = [];
    for (const condition of this.querySelectorAll('.condition')) {
      if (condition.changed) {
        condition.reset(false);
        this._updateCustomConditionVisibility(condition);
        resetConditions.push(condition);
      }
    }
    const hierarchySelection = this.querySelector('.hierarchy-selection');
    const resetHierarchies = hierarchySelection ? hierarchySelection.resetAllHierarchies(true) : [];
    const conditionIds = resetConditions.map(condition => condition.getAttribute('persistence-id'));
    this._onChange(this.initialConditionValuesSetHash, {
      conditionIds,
      hierarchyNames: resetHierarchies,
      oldConditionValuesSetHash: this.conditionValuesSetHash
    });
    if (resetConditions.length > 0) {
      this.querySelectorAll('d-condition-wrapper').forEach(wrapper => wrapper.updateResetBtnVisibility());
    }
    this._updateResetAllVisibility();
    this._updateConditionEnabledState();
    for (const condition of resetConditions) {
      this._updateConditionValidity(condition);
    }
  }

  _onConditionChanged (event) {
    event.stopPropagation();
    let condition = this.querySelector(`.condition[persistence-id="${event.detail.conditionId}"]`);
    if (!condition) {
      // when condition is changed on flyout then the event contains the conditions
      condition = event.detail.condition;
    }
    this._updateConditionValidity(condition);
    this._updateConditionEnabledState();
    this._updateResetAllVisibility();
    this._updateCustomConditionVisibility(condition);
    this._onChange(
      event.detail.conditionValuesSetHash,
      {
        conditionIds: [ event.detail.conditionId ],
        hierarchyNames: [],
        eagerlyContextSensitiveData: event.detail.eagerlyContextSensitiveData,
        oldConditionValuesSetHash: event.detail.oldConditionValuesSetHash
      });
  }

  _onHierarchyChanged (event) {
    this._updateResetAllVisibility();
    this._dispatchChangeEvent(this.conditionValuesSetHash, {
      conditionIds: [],
      hierarchyNames: [ event.detail.name ]
    });
  }

  _onChange (conditionValuesSetHash, { conditionIds, hierarchyNames = [], eagerlyContextSensitiveData = {}, oldConditionValuesSetHash = null } = {}) {
    if (this.isUpdateParams) {
      setParam({ paramName: PARAMETER_CONDITION_HASH, paramValue: conditionValuesSetHash });
    } else {
      this.#hash = conditionValuesSetHash;
    }
    this._dispatchChangeEvent(conditionValuesSetHash, {
      conditionIds,
      hierarchyNames,
      eagerlyContextSensitiveData,
      oldConditionValuesSetHash
    });
  }

  _dispatchChangeEvent (conditionValuesSetHash, { conditionIds, hierarchyNames = [], eagerlyContextSensitiveData = {}, oldConditionValuesSetHash = null } = {}) {
    const eventDetail = {
      conditionValuesSetHash,
      conditionIds,
      hierarchyNames,
      eagerlyContextSensitiveData,
      oldConditionValuesSetHash
    };
    const canceled = !this.dispatchEvent(new CustomEvent(CONDITIONS_HIERARCHIES_CHANGE, {
      cancelable: true,
      detail: eventDetail
    }));
    if (!canceled) {
      this.dispatchEvent(new CustomEvent(CHANGED, { detail: eventDetail }));
    }
  }

  _updateCustomConditionVisibility (condition) {
    if (!condition.custom) {
      return;
    }
    const wrapper = condition.closest('.d-condition-wrapper');
    wrapper.hidden = condition.isEmpty;
    const group = condition.closest('.d-condition-group');
    if (!group) {
      return;
    }
    const allEmpty = [ ...group.querySelectorAll('.condition[custom="true"]') ].every(c => c.isEmpty);
    group.hidden = allEmpty;
  }

  _updateConditionValidity (condition) {
    this.#validity.valid = condition.reportValidity();
    const group = condition.closest(conditionGroupSelector);
    if (group) {
      this.#validity.valid = group.reportValidity() && this.#validity.valid;
    }
  }

  _updateAllConditionsValidity () {
    this.#validity.valid = true;
    for (const conditionOrGroup of this.conditionsAndConditionGroups) {
      this.#validity.valid = conditionOrGroup.reportValidity() && this.#validity.valid;
    }
  }

  _updateConditionEnabledState () {
    if (this._enablingConditions.length === 0 && this._singleSelectionGroups.length === 0) {
      return;
    }
    // Each condition, that is mentioned in an enables array is added to the disabled set first.
    const enablees = this._enablingConditions
      .flatMap(condition => condition.enables);
    const disabledConditions = new Set();
    for (const enablee of enablees) {
      disabledConditions.add(enablee);
    }
    // A condition is enabled if there's at least one enabling condition with a value.
    for (const enablingCondition of this._enablingConditions) {
      if (enablingCondition.hasValue) {
        for (const enablee of enablingCondition.enables) {
          disabledConditions.delete(enablee);
        }
      }
    }
    // For conditionGroups with the attribute singleSelection we look if a contained condition
    // has a value. If so we disable all conditions in the group, but the first one with a value.
    for (const group of this._singleSelectionGroups) {
      const conditionsInGroup = [ ...group.querySelectorAll('.condition') ];
      if (group.hasHiddenConditionsWithValues) {
        conditionsInGroup.forEach(condition => disabledConditions.add(condition.conditionName));
      } else {
        let foundValue = false;
        if (conditionsInGroup.some(condition => condition.hasValue)) {
          for (const condition of conditionsInGroup) {
            if (condition.hasValue && !foundValue) {
              foundValue = true;
            } else {
              disabledConditions.add(condition.conditionName);
            }
          }
        }
      }
    }
    // Finally we know which conditions are enabled and which are disabled.
    for (const condition of this.querySelectorAll('.condition')) {
      const enabled = !disabledConditions.has(condition.conditionName);
      if (!enabled && condition.enabled) {
        condition.conditionWrapper.resetCondition();
      }
      condition.enabled = enabled;
    }
  }

  get conditionsAndConditionGroups () {
    return [ ...this.querySelectorAll(`${conditionGroupSelector}, .condition`) ];
  }

  _updateResetAllVisibility () {
    if (this._resetAllBtn) {
      const hierarchySelection = this.querySelector('.hierarchy-selection');
      this._resetAllBtn.hidden = ![ ...this.querySelectorAll('.condition') ].some(c => c.changed)
        && !(hierarchySelection && hierarchySelection.dirty);
    }
  }

  _wireToggleFilterButton () {
    const toggleFilterBtn = document.querySelector('.toggle-filter');
    const filterSidebar = document.querySelector('.contentWithFullLayout .d-sidebar');

    // Using throttling because of FF bug: https://bugzilla.mozilla.org/show_bug.cgi?id=1544889
    on(document.body, 'pointerup', throttle(() => {
      filterSidebar.open = !filterSidebar.open;
    }, MAX_TIME_POINTERUP_INTERVAL, { trailing: false }), { delegate: '.toggle-filter' });
    if (toggleFilterBtn) {
      toggleFilterBtn.setAttribute('aria-pressed', filterSidebar.open);
    }
    if (filterSidebar) {
      filterSidebar.addEventListener('change:open', () => onSidebarOpenChange(
        toggleFilterBtn !== null ? toggleFilterBtn : document.querySelector('.toggle-filter'),
        filterSidebar,
        toggleFilterBtn === null));
    }
  }

  onChangeHierarchyVisibility (event) {
    const hierarchySelection = this.querySelector('.hierarchy-selection');
    const tableColumnNamesWithVisibleIndicator = event.detail;
    hierarchySelection.updateHierarchySelectVisibility(hierarchyName => tableColumnNamesWithVisibleIndicator.find(({ name }) => name === hierarchyName).visible);

    storeRequestParameterValue(tableColumnNamesWithVisibleIndicator)
      .then(urlSearchParams => {
        if (this.isUpdateParams) {
          setParam({ paramName: 'columns', paramValue: urlSearchParams });
        }
      })
      .catch(err => {
        logger.error(`error while storing column settings in backend after hierarchy visibility change: ${err}`);
      });
  }

  onChangeFilterCondition (event) {
    this._onConditionChanged(event);
  }

  onChangeHierarchy (event) {
    this._onHierarchyChanged(event);
  }

  _toggleSection (header, section) {
    section.hidden = !section.hidden;
    header.setAttribute('aria-expanded', (!section.hidden).toString());
  }

}

customElements.define(NAME, FilterController);

function onSidebarOpenChange (toggleFilterBtn, filterContainer, isToggleFilterBtnInFlyout = false) {
  if (!isToggleFilterBtnInFlyout) {
    toggleFilterBtn.setAttribute('aria-pressed', filterContainer.open);
  }
  refresh();
}

function refresh () {
  [ ...document.querySelectorAll('.d-chart') ].forEach(chart => chart.refresh());
  [ ...document.querySelectorAll('.tableResultsTable d-table') ].forEach(dTable => dTable.resize());
}

function disableConditions (enabledConditions) {
  enabledConditions.map(condition => { condition.enabled = false; });
}

function setDelayNotification (conditions, isDelayed) {
  conditions.map(condition => condition.setDelayNotification(isDelayed));
}
